import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { showError } from '../../../shared/ui/appToast';

export const MASTERS_PAGE_SIZE_STORAGE_KEY = 'masters_page_size';
export const DEFAULT_MASTERS_PAGE_SIZE = 10;

export type MastersPageSize = 5 | 10;

type FilterState = 'all' | 'active' | 'inactive';

type UseMastersPaginationParams<T> = {
  fetchCount: (filterState: FilterState, search?: string) => Promise<number>;
  fetchPage: (params: {
    filterState: FilterState;
    search?: string;
    limit: number;
    offset: number;
  }) => Promise<T[]>;
  filterState: FilterState;
  searchQuery: string;
  storageKey: string;
};

type UseMastersPaginationReturn<T> = {
  items: T[];
  pageIndex: number;
  pageSize: MastersPageSize;
  totalCount: number | null;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  setPageSize: (size: MastersPageSize) => void;
  nextPage: () => void;
  previousPage: () => void;
  reload: () => void;
};

export function useMastersPagination<T>({
  fetchCount,
  fetchPage,
  filterState,
  searchQuery,
  storageKey,
}: UseMastersPaginationParams<T>): UseMastersPaginationReturn<T> {
  const opGenRef = useRef(0);
  const [pageSize, setPageSizeState] = useState<MastersPageSize>(DEFAULT_MASTERS_PAGE_SIZE);
  const [sizeHydrated, setSizeHydrated] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(storageKey).then((raw) => {
      if (!alive) return;
      if (raw === '5' || raw === '10') {
        setPageSizeState(raw === '5' ? 5 : 10);
      }
      setSizeHydrated(true);
    });
    return () => {
      alive = false;
    };
  }, [storageKey]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    const handle = setTimeout(() => setDebouncedSearch(trimmed), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    setPageIndex(0);
  }, [filterState, debouncedSearch]);

  useEffect(() => {
    void (async () => {
      try {
        const count = await fetchCount(filterState, debouncedSearch);
        setTotalCount(count);
      } catch (fetchError) {
        console.warn('[useMastersPagination] Error fetching count', fetchError);
        setTotalCount(null);
      }
    })();
  }, [fetchCount, filterState, debouncedSearch, reloadToken]);

  useEffect(() => {
    if (!sizeHydrated) return;

    const opId = ++opGenRef.current;
    const limit = pageSize;
    const offset = pageIndex * limit;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPage({ filterState, search: debouncedSearch, limit, offset });
        if (opId !== opGenRef.current) return;
        setItems(data);
        setHasMore(data.length === limit);
      } catch (fetchError) {
        if (opId !== opGenRef.current) return;
        const message =
          fetchError instanceof Error ? fetchError.message : 'No se pudo cargar la lista.';
        console.warn('[useMastersPagination] Error fetching page', fetchError);
        setItems([]);
        setHasMore(false);
        setError(message);
        showError('Error al cargar', message);
      } finally {
        if (opId === opGenRef.current) {
          setLoading(false);
        }
      }
    };

    void run();
  }, [fetchPage, filterState, debouncedSearch, pageIndex, pageSize, sizeHydrated, reloadToken]);

  useEffect(() => {
    if (totalCount === null || loading) return;
    const offset = pageIndex * pageSize;
    setHasMore(offset + items.length < totalCount);
  }, [totalCount, loading, pageIndex, pageSize, items.length]);

  const setPageSize = useCallback((size: MastersPageSize) => {
    setPageSizeState(size);
    void AsyncStorage.setItem(storageKey, String(size));
    setPageIndex(0);
  }, [storageKey]);

  const nextPage = useCallback(() => {
    if (!hasMore || loading) return;
    setPageIndex((prev) => prev + 1);
  }, [hasMore, loading]);

  const previousPage = useCallback(() => {
    if (loading) return;
    setPageIndex((prev) => Math.max(0, prev - 1));
  }, [loading]);

  const reload = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  return {
    items,
    pageIndex,
    pageSize,
    totalCount,
    hasMore,
    loading,
    error,
    setPageSize,
    nextPage,
    previousPage,
    reload,
  };
}
